import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { File, Download, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Attachment {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  mime_type: string;
}

interface AttachmentDisplayProps {
  attachment: Attachment;
}

export function AttachmentDisplay({ attachment }: AttachmentDisplayProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);

  const fileType = attachment.mime_type.split('/')[0];
  const isImage = fileType === 'image';
  const isVideo = fileType === 'video';
  const isAudio = fileType === 'audio';
  const isPDF = attachment.mime_type === 'application/pdf';

  const getFileUrl = async () => {
    setLoading(true);
    try {
      const { data } = await supabase.storage
        .from('chat-attachments')
        .createSignedUrl(attachment.file_path, 3600); // 1 hour expiry

      if (data) {
        setPreviewUrl(data.signedUrl);
        if (isImage || isVideo || isAudio || isPDF) {
          setShowPreview(true);
        } else {
          // Download file
          window.open(data.signedUrl, '_blank');
        }
      }
    } catch (error) {
      console.error('Error getting file URL:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async () => {
    if (previewUrl) {
      const link = document.createElement('a');
      link.href = previewUrl;
      link.download = attachment.file_name;
      link.click();
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <>
      <div className="flex items-center gap-2 p-2 border border-border rounded-lg bg-background/50 hover:bg-background/80 transition-colors max-w-xs">
        <File className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{attachment.file_name}</p>
          <p className="text-xs text-muted-foreground">{formatSize(attachment.file_size)}</p>
        </div>
        <Button
          onClick={getFileUrl}
          disabled={loading}
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
        >
          <ExternalLink className="h-4 w-4" />
        </Button>
      </div>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="truncate">{attachment.file_name}</span>
              <Button
                onClick={downloadFile}
                variant="outline"
                size="sm"
                className="ml-4"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex items-center justify-center overflow-auto max-h-[70vh]">
            {previewUrl && (
              <>
                {isImage && (
                  <img 
                    src={previewUrl} 
                    alt={attachment.file_name}
                    className="max-w-full h-auto rounded-lg"
                  />
                )}
                {isVideo && (
                  <video 
                    src={previewUrl} 
                    controls 
                    className="max-w-full h-auto rounded-lg"
                  />
                )}
                {isAudio && (
                  <audio 
                    src={previewUrl} 
                    controls 
                    className="w-full"
                  />
                )}
                {isPDF && (
                  <iframe
                    src={previewUrl}
                    className="w-full h-[70vh] rounded-lg"
                    title={attachment.file_name}
                  />
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}